import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  ujmsConnectors, ujmsCreateConnector, ujmsUpdateConnector, ujmsDeleteConnector, ujmsTestConnector,
  ujmsDeliveries, ujmsReplayDelivery, ujmsIntegrationStats, ujmsEventTypes,
} from "../../services/ujmsApi";
import { Panel, Badge, thCls, tdCls, inputCls, selectCls, btnPrimary, btnGhost, Field, fmtDT, cls } from "../ui";
import { Webhook, Trash2, RefreshCw, Radio, Plug } from "lucide-react";

const err = (e, fallback) => toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : fallback);

export default function IntegrationHub() {
  const [stats, setStats] = useState(null);
  const [connectors, setConnectors] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [nc, setNc] = useState({ name: "", target_url: "", event_types: "", secret: "", description: "" });

  const load = useCallback(() => {
    ujmsIntegrationStats().then((r) => setStats(r.data)).catch(() => {});
    ujmsConnectors().then((r) => setConnectors(r.data)).catch(() => {});
    ujmsDeliveries(statusFilter ? { status: statusFilter } : {}).then((r) => setDeliveries(r.data)).catch(() => {});
    ujmsEventTypes().then((r) => setEventTypes(r.data)).catch(() => {});
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    try {
      const event_types = nc.event_types.split(",").map((t) => t.trim()).filter(Boolean);
      await ujmsCreateConnector({ ...nc, event_types });
      toast.success(`Connector '${nc.name}' registered`);
      setNc({ name: "", target_url: "", event_types: "", secret: "", description: "" }); load();
    } catch (e) { err(e, "Failed to create connector"); }
  };
  const toggle = async (c) => {
    try { await ujmsUpdateConnector(c.id, { enabled: !c.enabled }); toast.success(c.enabled ? "Connector disabled" : "Connector enabled"); load(); }
    catch (e) { err(e, "Failed"); }
  };
  const del = async (c) => {
    try { await ujmsDeleteConnector(c.id); toast.success("Connector deleted"); load(); }
    catch (e) { err(e, "Failed"); }
  };
  const ping = async (c) => {
    try {
      const r = await ujmsTestConnector(c.id);
      if (r.data.status === "DELIVERED") toast.success(`PING delivered (HTTP ${r.data.http_status})`);
      else toast.error(`PING failed — ${r.data.error || r.data.status}`);
      load();
    } catch (e) { err(e, "Test failed"); }
  };
  const replay = async (d) => {
    try {
      const r = await ujmsReplayDelivery(d.id);
      if (r.data.status === "DELIVERED") toast.success("Replay delivered");
      else toast.error(`Replay ${r.data.status} — ${r.data.error || ""}`);
      load();
    } catch (e) { err(e, "Replay failed"); }
  };

  const echoUrl = `${process.env.REACT_APP_BACKEND_URL}/api/ujms/integrations/echo`;

  return (
    <div className="space-y-4" data-testid="integration-hub">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 flex items-center gap-2"><Webhook className="w-6 h-6" /> Integration Hub</h1>
        <p className="text-sm text-slate-500">Outbound connectors deliver every domain event to partner agencies with signed payloads, automatic retry and a dead-letter queue.</p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3" data-testid="integration-stats">
          {[["Connectors", stats.connectors], ["Enabled", stats.connectors_enabled],
            ["Pending", stats.deliveries.PENDING], ["Delivered", stats.deliveries.DELIVERED],
            ["Failed (retrying)", stats.deliveries.FAILED], ["Dead Letter", stats.deliveries.DEAD]].map(([label, n]) => (
            <div key={label} className="bg-white border border-slate-200 rounded-md px-4 py-3">
              <div className={cls("text-2xl font-semibold", label === "Dead Letter" && n > 0 ? "text-red-600" : "text-slate-900")}>{n}</div>
              <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
            </div>
          ))}
        </div>
      )}

      <Panel title="Register a connector" testId="create-connector-panel">
        <div className="p-3 flex flex-wrap items-end gap-2">
          <Field label="Name" required><input className={`${inputCls} w-44`} value={nc.name} onChange={(e) => setNc({ ...nc, name: e.target.value })} placeholder="State Police CJIS" data-testid="connector-name" /></Field>
          <Field label="Target URL" required><input className={`${inputCls} w-80 font-mono text-xs`} value={nc.target_url} onChange={(e) => setNc({ ...nc, target_url: e.target.value })} placeholder="https://agency.example.gov/webhook" data-testid="connector-url" /></Field>
          <Field label="Event types (comma separated — empty = all)"><input className={`${inputCls} w-72 font-mono text-xs`} value={nc.event_types} onChange={(e) => setNc({ ...nc, event_types: e.target.value })} placeholder="WARRANT_ISSUED, CASE_DISPOSED" data-testid="connector-events" /></Field>
          <Field label="HMAC secret"><input className={`${inputCls} w-40`} value={nc.secret} onChange={(e) => setNc({ ...nc, secret: e.target.value })} data-testid="connector-secret" /></Field>
          <button className={btnPrimary} disabled={!nc.name || !nc.target_url} onClick={create} data-testid="connector-create-btn"><Plug className="w-3.5 h-3.5" /> Register</button>
        </div>
        <p className="px-3 pb-3 text-xs text-slate-400">
          Demo sink: <span className="font-mono">{echoUrl}</span> (append <span className="font-mono">?fail=1</span> to simulate an outage and exercise retry → dead letter).
          Known event types: {eventTypes.slice(0, 8).join(", ")}… ({eventTypes.length} total)
        </p>
      </Panel>

      <Panel title="Connectors" testId="connectors-panel">
        <table className="w-full border-collapse">
          <thead><tr><th className={thCls}>Name</th><th className={thCls}>Target</th><th className={thCls}>Events</th><th className={thCls}>Deliveries</th><th className={thCls}>Status</th><th className={thCls}>Actions</th></tr></thead>
          <tbody>
            {connectors.map((c) => (
              <tr key={c.id} className="hover:bg-slate-50" data-testid={`connector-row-${c.name.replace(/\s+/g, "-").toLowerCase()}`}>
                <td className={`${tdCls} font-medium`}>{c.name}<div className="text-xs text-slate-400">{c.description}</div></td>
                <td className={`${tdCls} font-mono text-[11px] max-w-xs truncate`}>{c.target_url}</td>
                <td className={`${tdCls} text-xs`}>{c.event_types?.length ? c.event_types.join(", ") : <span className="text-slate-400">all events</span>}</td>
                <td className={`${tdCls} font-mono text-xs`}>
                  <span className="text-emerald-700">{c.stats?.DELIVERED ?? 0}✓</span>{" · "}
                  <span className="text-amber-700">{(c.stats?.PENDING ?? 0) + (c.stats?.FAILED ?? 0)}⟳</span>{" · "}
                  <span className="text-red-700">{c.stats?.DEAD ?? 0}✗</span>
                </td>
                <td className={tdCls}><Badge value={c.enabled ? "ENABLED" : "DISABLED"} /></td>
                <td className={`${tdCls} whitespace-nowrap`}>
                  <div className="flex gap-1.5">
                    <button className={btnGhost} onClick={() => ping(c)} title="Send PING" data-testid={`connector-ping-${c.name.replace(/\s+/g, "-").toLowerCase()}`}><Radio className="w-3.5 h-3.5" /></button>
                    <button className={btnGhost} onClick={() => toggle(c)} data-testid={`connector-toggle-${c.name.replace(/\s+/g, "-").toLowerCase()}`}>{c.enabled ? "Disable" : "Enable"}</button>
                    <button className="text-red-600 hover:text-red-800 px-1" onClick={() => del(c)} data-testid={`connector-delete-${c.name.replace(/\s+/g, "-").toLowerCase()}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!connectors.length && <tr><td className={`${tdCls} text-slate-400`} colSpan={6}>No connectors registered.</td></tr>}
          </tbody>
        </table>
      </Panel>

      <div className="flex gap-2 items-end">
        <Field label="Filter deliveries">
          <select className={`${selectCls} w-44`} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="delivery-status-filter">
            <option value="">All statuses</option>
            {["PENDING", "DELIVERED", "FAILED", "DEAD"].map((s) => <option key={s}>{s}</option>)}
          </select>
        </Field>
        <button className={btnGhost} onClick={load} data-testid="deliveries-refresh"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
      </div>

      <Panel title="Delivery log — failed deliveries retry with exponential backoff; exhausted deliveries land in the dead-letter queue" testId="deliveries-panel">
        <div className="max-h-[440px] overflow-y-auto">
          <table className="w-full border-collapse">
            <thead><tr><th className={thCls}>Queued</th><th className={thCls}>Connector</th><th className={thCls}>Event</th><th className={thCls}>Status</th><th className={thCls}>Attempts</th><th className={thCls}>Last Error</th><th className={thCls}></th></tr></thead>
            <tbody>
              {deliveries.map((d) => (
                <tr key={d.id} className="hover:bg-slate-50" data-testid={`delivery-row-${d.id}`}>
                  <td className={`${tdCls} font-mono text-xs whitespace-nowrap`}>{fmtDT(d.created_at)}</td>
                  <td className={`${tdCls} text-xs`}>{d.connector_name}</td>
                  <td className={tdCls}><Badge value={d.event_type} /></td>
                  <td className={tdCls}><Badge value={d.status} testId={`delivery-status-${d.id}`} /></td>
                  <td className={`${tdCls} font-mono text-xs`}>{d.attempts}/{d.max_attempts}</td>
                  <td className={`${tdCls} text-xs text-red-700 max-w-xs truncate`}>{d.last_error || "—"}</td>
                  <td className={tdCls}>
                    {(d.status === "DEAD" || d.status === "FAILED") && (
                      <button className={btnGhost} onClick={() => replay(d)} data-testid={`delivery-replay-${d.id}`}>Replay</button>
                    )}
                  </td>
                </tr>
              ))}
              {!deliveries.length && <tr><td className={`${tdCls} text-slate-400`} colSpan={7}>No deliveries yet — register a connector and let case activity flow.</td></tr>}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
